import classNames from 'classnames';
import React from 'react';
import { statusToneStyle, UDStatusTone } from 'ud-ui/components/status-tone';

/**
 * - `solid` — насыщенная заливка тона и белая иконка: метка категории, которую видно издалека;
 * - `soft` — светлая заливка тона и иконка цвета тона: спокойный значок при тексте.
 */
export type UDIconTileVariant = 'solid' | 'soft';

export interface UDIconTileProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'color'> {
  /** Иконка (обычно из lucide-react); её размер задаёт вызывающий. */
  icon: React.ReactNode;
  tone: UDStatusTone;
  variant?: UDIconTileVariant;
  /** Сторона квадрата, px. */
  size?: number;
}

/**
 * Размер и форма плитки по умолчанию. Экспортируется для скелетонов: плашка меряется теми же значениями,
 * что и живая плитка (`.claude/docs/skeleton-kit.md`).
 */
export const UD_ICON_TILE_METRICS = { size: 40, className: 'rounded-card shrink-0' } as const;

/**
 * Иконка на тонированной квадратной подложке.
 *
 * Цвет — переменные тона (`ud-ui/components/status-tone`), общие с бейджем статуса и фильтр-чипом: тон
 * `error` у плитки, бейджа и чипа один и тот же, и меняется в одном месте. Плитка декоративная
 * (`aria-hidden`): смысл несёт подпись рядом, цвет не единственный его носитель.
 */
const UDIconTile = ({
  icon,
  tone,
  variant = 'soft',
  size = UD_ICON_TILE_METRICS.size,
  className,
  style,
  ...rest
}: UDIconTileProps) => {
  return (
    <div
      {...rest}
      aria-hidden
      data-tone={tone}
      data-variant={variant}
      style={{ ...statusToneStyle(tone), width: size, height: size, ...style }}
      className={classNames(
        UD_ICON_TILE_METRICS.className,
        'flex items-center justify-center',
        variant === 'solid' ? 'text-inkOnDark bg-(--tone-solid)' : 'bg-(--tone-surface) text-(--tone-ink)',
        className,
      )}
    >
      {icon}
    </div>
  );
};

export default React.memo(UDIconTile);
